/**
 * The reports' shared filters (BUILD_PLAN.md section 13; ADR 0123), kept in
 * the URL so a report can be bookmarked, and kept when another report is
 * chosen: `?start=YYYY-MM-DD&end=YYYY-MM-DD&tech=<id>&client=<id>`, each
 * `tech` and `client` repeated as needed. Utilization's `by` and the Open
 * tickets report's `state` are their own, and are kept as the filters change.
 *
 * The days are each tech's own (section 17.1): the API reads them in each
 * tech's timezone. Without dates, the last 7 days to today in the viewer's.
 * A tech's reports are of their own work (section 6.2), so their filters
 * name no tech, whatever a shared link says: the API would refuse another's.
 */
import { useSearchParams } from "react-router";

import type { ReportFilters } from "../../api/types";
import { useToday } from "../../app/useSelectedDay";
import { useSession } from "../../app/session-context";
import { addDays, isIsoDay } from "../../lib/dates";

export const DEFAULT_DAYS = 7;
// What one report chooses for itself, kept as the shared filters change:
// utilization's `by` and the Open tickets report's `state`.
const REPORT_OWN = ["by", "state"] as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function ids(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => UUID.test(value)))];
}

/** The filters a URL's query names; a malformed date or ID is left out. */
export function filtersFrom(params: URLSearchParams, today: string): ReportFilters {
  const start = params.get("start");
  const end = params.get("end");
  const last = end !== null && isIsoDay(end) ? end : today;
  return {
    start: start !== null && isIsoDay(start) ? start : addDays(last, -(DEFAULT_DAYS - 1)),
    end: last,
    userIds: ids(params.getAll("tech")),
    clientIds: ids(params.getAll("client")),
  };
}

/** The URL's query for `filters`. */
export function searchFor(filters: ReportFilters): URLSearchParams {
  const params = new URLSearchParams({ start: filters.start, end: filters.end });
  for (const id of filters.userIds) {
    params.append("tech", id);
  }
  for (const id of filters.clientIds) {
    params.append("client", id);
  }
  return params;
}

/** The filters in the URL, and a way to change them there. */
export function useReportFilters(): [ReportFilters, (next: ReportFilters) => void] {
  const { me } = useSession();
  const today = useToday(me.timezone);
  const [params, setParams] = useSearchParams();
  const filters = filtersFrom(params, today);
  return [
    me.role === "owner" ? filters : { ...filters, userIds: [] },
    (next) => {
      // A report's own choices (utilization's `by`, open tickets' `state`)
      // stay as the filters change.
      const kept = searchFor(next);
      for (const own of REPORT_OWN) {
        for (const value of params.getAll(own)) {
          kept.append(own, value);
        }
      }
      setParams(kept, { replace: true });
    },
  ];
}
